import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, Compass, CircleUser } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePwaInstall } from "@/lib/pwa";
import { InstallBanner, IosInstallSheet } from "@/components/InstallPrompt";

const DISMISS_KEY = "aip_install_dismissed";

// Routes where the native app-shell bottom bar should NOT appear
// (marketing, auth, mid-funnel, and the immersive lesson player).
const HIDE_PREFIXES = [
  "/login", "/register", "/quiz", "/subscribe", "/pricing", "/survey",
  "/terms", "/privacy", "/youtube-compliance", "/r/", "/lesson",
];

// Simplified 3-tab IA: Learn (your path) · Explore (tools + AI universe) · Me (progress & settings)
const TABS = [
  { label: "Learn", path: "/dashboard", icon: GraduationCap, testid: "bottom-nav-learn",
    match: (p) => p === "/dashboard" },
  { label: "Explore", path: "/explore", icon: Compass, testid: "bottom-nav-explore",
    match: (p) => ["/explore", "/tools", "/universe", "/whats-new"].some((x) => p.startsWith(x)) },
  { label: "Me", path: "/me", icon: CircleUser, testid: "bottom-nav-me",
    match: (p) => ["/me", "/account", "/analytics", "/prompts", "/admin"].some((x) => p.startsWith(x)) },
];

export function BottomNav() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [iosOpen, setIosOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => (typeof localStorage !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1")
  );
  const install = usePwaInstall();
  const path = location.pathname;

  const hidden = !user || HIDE_PREFIXES.some((p) => path === p || path.startsWith(p + "/"));
  // Toggle a body class so the global mobile bottom-padding (in index.css) only
  // applies on screens that actually render the bottom bar.
  useEffect(() => {
    document.body.classList.toggle("has-bottomnav", !hidden);
    return () => document.body.classList.remove("has-bottomnav");
  }, [hidden]);

  if (hidden) return null;

  const canShowInstall = !install.standalone && (install.canInstall || install.ios);
  const doInstall = async () => {
    if (install.canInstall) {
      const outcome = await install.promptInstall();
      if (outcome === "accepted") { localStorage.setItem(DISMISS_KEY, "1"); setBannerDismissed(true); }
    } else if (install.ios) {
      setIosOpen(true);
    }
  };
  const dismissBanner = () => { localStorage.setItem(DISMISS_KEY, "1"); setBannerDismissed(true); };

  return (
    <>
      <nav data-testid="bottom-nav"
        className="md:hidden fixed bottom-4 left-4 right-4 z-50 rounded-full glass shadow-[0_16px_48px_-16px_rgba(2,32,71,0.3)] border border-border/70">
        <div className="flex items-stretch justify-around px-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = t.match(path);
            return (
              <button key={t.path} data-testid={t.testid} onClick={() => navigate(t.path)}
                className="tap relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[58px] outline-none">
                {active && (
                  <motion.span layoutId="bottomnav-pill"
                    className="absolute top-1.5 h-9 w-16 rounded-full bg-primary/10"
                    transition={{ type: "spring", stiffness: 480, damping: 36 }} />
                )}
                <Icon className={`h-[22px] w-[22px] relative z-10 transition-colors ${active ? "text-primary" : "text-muted-foreground"}`}
                  strokeWidth={active ? 2.6 : 2} />
                <span className={`relative z-10 text-[10px] font-semibold tracking-wide transition-colors ${active ? "text-primary" : "text-muted-foreground"}`}>
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      <InstallBanner show={canShowInstall && !bannerDismissed} onInstall={doInstall} onDismiss={dismissBanner} />
      <IosInstallSheet open={iosOpen} onOpenChange={setIosOpen} />
    </>
  );
}
